import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type CreateRoleBody,
  type PatchRoleBody,
  type PublicRole,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { useRetiredMarksCatalog } from "../../lib/useRetiredMarksCatalog.js";
import {
  roleByIdPath,
  rolesPath,
} from "../../lib/api-paths.js";
import { ErrorBanner, InfoBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import { Drawer } from "../../components/Drawer.js";
import { Table, type ColumnDef } from "../../components/Table.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  TextInput,
} from "../../components/FormField.js";
import { FeishuDeptTree } from "./FeishuDeptTree.js";
import {
  EMPTY_POLICY_VALUE,
  hydratePolicyValue,
  PolicyEditor,
  serializePolicyValue,
  type PolicyEditorValue,
} from "./role-dosia/PolicyEditor.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "./context.js";

type ModalKind =
  | { kind: "create" }
  | { kind: "edit"; role: PublicRole }
  | { kind: "confirm-delete"; role: PublicRole }
  | null;

export function RolesPanel(): JSX.Element {
  const { t } = useTranslation();
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const isOrgAdmin = auth?.scope === "org-admin";
  const [modal, setModal] = useState<ModalKind>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useApiQuery<PublicRole[]>(
    rolesPath(org.code),
    [org.code],
  );

  const handleCreate = async (body: CreateRoleBody): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("POST", rolesPath(org.code), { body, token: auth.token });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "创建失败");
    }
  };

  const handlePatch = async (
    role: PublicRole,
    body: PatchRoleBody,
  ): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("PATCH", roleByIdPath(org.code, role.id), {
        body,
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      if (err instanceof ApiError && err.code === "feishu-app-bound-to-agent") {
        // N-83 D10：同一个飞书应用只能绑角色或 Agent 之一；服务端带回占用它的 Agent 名。
        const d = err.details;
        const agentName =
          d && typeof d === "object" && typeof (d as { agentName?: unknown }).agentName === "string"
            ? (d as { agentName: string }).agentName
            : "";
        setActionError(t("admin.roles.feishuBot.appBoundToAgent", { name: agentName }));
      } else if (err instanceof ApiError && err.code === "forbidden-field") {
        setActionError(
          "沙盒 / hook / 系统策略等平台管理字段仅 DOSIA 运营可改，本次保存被拒绝",
        );
      } else {
        setActionError(err instanceof ApiError ? err.message : "保存失败");
      }
    }
  };

  const handleDelete = async (role: PublicRole): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("DELETE", roleByIdPath(org.code, role.id), {
        token: auth.token,
      });
      setActionError(null);
      setModal(null);
      await refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(
          err.code === "role-still-in-use"
            ? "该角色仍有用户引用，请先 Unassign 用户或重新指派"
            : err.message,
        );
      } else {
        setActionError("删除失败");
      }
    }
  };

  const columns: ColumnDef<PublicRole>[] = [
    { header: "code", cellClassName: "font-mono", render: (r) => r.code },
    { header: "名称", render: (r) => r.name },
    {
      header: "描述",
      render: (r) => r.description ?? <span className="text-dosia-text-dim">—</span>,
    },
    {
      header: "建议部门",
      render: (r) =>
        r.suggestedFeishuDeptIds.length > 0 ? (
          <span className="text-xs">{r.suggestedFeishuDeptIds.length} 个</span>
        ) : (
          <span className="text-dosia-text-dim text-xs">未设置</span>
        ),
    },
    {
      header: "飞书 bot",
      render: (r) =>
        r.feishuBot?.enabled && r.feishuBot.hasSecret ? (
          <span className="text-xs text-emerald-700">已接入</span>
        ) : (
          <span className="text-dosia-text-dim text-xs">未接入</span>
        ),
    },
    {
      header: "操作",
      width: "w-40",
      render: (r) => (
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs text-dosia-accent hover:underline"
            onClick={() => setModal({ kind: "edit", role: r })}
          >
            编辑
          </button>
          {r.isSystem ? null : (
            <button
              type="button"
              className="text-xs text-amber-700 hover:underline"
              onClick={() => setModal({ kind: "confirm-delete", role: r })}
            >
              删除
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-dosia-text-secondary">
          角色定义企业员工的工作面 — 含 dosia desktop workspace 配置 + 飞书部门建议（用于
          Phase F Agent 推荐绑定）。
        </p>
        <PrimaryButton onClick={() => setModal({ kind: "create" })}>
          + 创建角色
        </PrimaryButton>
      </div>
      {error ? <ErrorBanner>{error.message}</ErrorBanner> : null}
      {actionError ? (
        <ErrorBanner onDismiss={() => setActionError(null)}>
          {actionError}
        </ErrorBanner>
      ) : null}
      <Table<PublicRole>
        columns={columns}
        rows={data ?? []}
        rowKey={(r) => r.id}
        loading={loading}
        empty={<Empty title="尚无角色" description="点右上角创建第一个" />}
      />

      {modal?.kind === "create" ? (
        <CreateRoleDialog
          onCancel={() => setModal(null)}
          onSubmit={handleCreate}
        />
      ) : null}
      {modal?.kind === "edit" ? (
        <EditRoleDrawer
          role={modal.role}
          highRiskReadOnly={isOrgAdmin}
          onCancel={() => setModal(null)}
          onSubmit={(body) => handlePatch(modal.role, body)}
        />
      ) : null}
      {modal?.kind === "confirm-delete" ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title="确认删除角色"
          footer={
            <>
              <SecondaryButton onClick={() => setModal(null)}>
                取消
              </SecondaryButton>
              <PrimaryButton onClick={() => void handleDelete(modal.role)}>
                确认删除
              </PrimaryButton>
            </>
          }
        >
          <p className="text-sm text-dosia-text">
            将永久删除角色 <span className="font-mono">{modal.role.code}</span>。
            若仍有用户绑定，操作会被服务端拒绝（409 role-still-in-use）。
          </p>
        </Modal>
      ) : null}
    </div>
  );
}

function CreateRoleDialog({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (body: CreateRoleBody) => Promise<void>;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ready = code.trim().length > 0 && name.trim().length > 0;
  return (
    <Modal
      open
      onClose={onCancel}
      title="创建角色"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={!ready || submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit({
                code: code.trim(),
                name: name.trim(),
                description: description || undefined,
              });
              setSubmitting(false);
            }}
          >
            创建
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="code" required hint="角色唯一标识；创建后不可改">
          <TextInput
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="media"
          />
        </FormField>
        <FormField label="名称" required>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="媒体"
          />
        </FormField>
        <FormField label="描述">
          <TextInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  );
}

const DOSIA_PLACEHOLDER = `{
  "templateId": "default",
  "skillPolicy": { ... },
  "sandboxConfig": { ... }
}`;

type EditTab = "basic" | "policy" | "depts" | "feishuBot";

function EditRoleDrawer({
  role,
  highRiskReadOnly = false,
  onCancel,
  onSubmit,
}: {
  role: PublicRole;
  /** org-admin：高危字段只读 + 隐藏裸 JSON 高级编辑（服务端 forbidden-field 403 兜底） */
  highRiskReadOnly?: boolean;
  onCancel: () => void;
  onSubmit: (body: PatchRoleBody) => Promise<void>;
}): JSX.Element {
  const [tab, setTab] = useState<EditTab>("basic");
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  // 「基础」tab JSON fallback：留空 = 不变更（沿用 role.dosia）
  const [dosiaJson, setDosiaJson] = useState(
    role.dosia ? JSON.stringify(role.dosia, null, 2) : "",
  );
  const [dosiaError, setDosiaError] = useState<string | null>(null);
  // 「dosia 策略」tab 结构化编辑值（从 role.dosia hydrate）
  const [policy, setPolicy] = useState<PolicyEditorValue>(() =>
    role.dosia ? hydratePolicyValue(role.dosia) : EMPTY_POLICY_VALUE,
  );
  const [policyError, setPolicyError] = useState<string | null>(null);
  // 分组默认里已下线的模型标记（N-88）：网关现行清单，拿不到 = undefined = 不标。
  const { org } = useOutletContext<OrgDetailContext>();
  const liveCatalog = useRetiredMarksCatalog(org.code);
  const [deptIds, setDeptIds] = useState<string[]>(role.suggestedFeishuDeptIds);
  // 「飞书机器人」tab：可开关 + App ID/Secret（secret 留空=不改原密文）
  const fbInitialEnabled = role.feishuBot?.enabled ?? false;
  const fbInitialAppId = role.feishuBot?.appId ?? "";
  const fbHasSecret = role.feishuBot?.hasSecret ?? false;
  const [feishuEnabled, setFeishuEnabled] = useState(fbInitialEnabled);
  const [feishuAppId, setFeishuAppId] = useState(fbInitialAppId);
  const [feishuAppSecret, setFeishuAppSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 模型可见性圈定 + 每模型参数候选/编辑已全部迁至统一「模型治理」tab（scope=角色）——
  // 本角色抽屉的 policy tab 不再触碰 modelSelection（P2c 单源收口，红线 #9）。

  type ParseResult =
    | { ok: true; value: unknown }
    | { ok: false; error: string };

  const parseDosiaJson = (raw: string): ParseResult => {
    if (!raw.trim()) return { ok: true, value: undefined };
    try {
      return { ok: true, value: JSON.parse(raw) as unknown };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "JSON parse failed",
      };
    }
  };

  const handleSave = async (): Promise<void> => {
    let dosia: unknown;
    // 提交时按当前活动 tab 的内容回写 dosia：
    // - basic tab：从 textarea JSON 解析（留空 → role.dosia 保留）
    // - policy tab：从结构化表单 serialize（高级 JSON 字段失败 → 阻止保存）
    if (tab === "policy") {
      // base = 编辑前 role.dosia：表单没建模的字段（name/agentRoleBindings…）透传不丢
      const result = serializePolicyValue(policy, role.dosia);
      if (!result.ok) {
        setPolicyError(result.error);
        return;
      }
      setPolicyError(null);
      dosia = result.dosia;
    } else {
      const result = parseDosiaJson(dosiaJson);
      if (!result.ok) {
        setDosiaError(result.error);
        return;
      }
      setDosiaError(null);
      dosia = result.value ?? role.dosia;
    }
    // feishuBot 仅在有变更时下发：secret 留空不覆盖原密文（后端「空=不改」约定）。
    const fbChanged =
      feishuEnabled !== fbInitialEnabled ||
      feishuAppId.trim() !== fbInitialAppId ||
      feishuAppSecret.trim() !== "";
    const feishuBot = fbChanged
      ? {
          enabled: feishuEnabled,
          appId: feishuAppId.trim(),
          ...(feishuAppSecret.trim() ? { appSecret: feishuAppSecret.trim() } : {}),
        }
      : undefined;
    setSubmitting(true);
    await onSubmit({
      name: name.trim(),
      description: description || undefined,
      dosia,
      suggestedFeishuDeptIds: deptIds,
      feishuBot,
    });
    setSubmitting(false);
  };

  const toggleDept = (id: string): void => {
    setDeptIds((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id],
    );
  };

  return (
    <Drawer
      open
      onClose={onCancel}
      title={`编辑角色 ${role.code}`}
      width={720}
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={submitting || dosiaError !== null || policyError !== null}
            onClick={() => void handleSave()}
          >
            保存
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* T1.4.3 · tab 切换：基础 / dosia 策略 / 飞书部门 */}
        <div className="flex border-b border-dosia-border">
          {(
            [
              ["basic", "基础"],
              ["policy", "dosia 策略"],
              ["depts", "飞书部门"],
              ["feishuBot", "飞书机器人"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                "px-3 py-1.5 text-sm border-b-2 -mb-px",
                tab === key
                  ? "border-dosia-accent text-dosia-text"
                  : "border-transparent text-dosia-text-secondary hover:text-dosia-text",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "basic" ? (
          <div className="flex flex-col gap-3">
            <FormField label="名称">
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormField>
            <FormField label="描述">
              <TextInput
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormField>
            {highRiskReadOnly ? (
              <InfoBanner>
                <span className="text-xs">
                  JSON 高级编辑仅平台超管可用；结构化字段去「dosia 策略」tab（
                  沙盒 / hook / 系统策略等平台管理字段只读）。
                </span>
              </InfoBanner>
            ) : (
              <>
                <InfoBanner>
                  <span className="text-xs">
                    JSON 编辑 — 高级路径，留空保留原值。结构化字段去「dosia 策略」tab。
                  </span>
                </InfoBanner>
                <FormField
                  label="role.dosia (JSON)"
                  error={dosiaError ?? undefined}
                  hint="留空 = 不变更"
                >
                  <TextArea
                    rows={14}
                    value={dosiaJson}
                    onChange={(e) => {
                      setDosiaJson(e.target.value);
                      setDosiaError(null);
                    }}
                    onBlur={() => {
                      if (!dosiaJson.trim()) {
                        setDosiaError(null);
                        return;
                      }
                      const result = parseDosiaJson(dosiaJson);
                      setDosiaError(result.ok ? null : result.error);
                    }}
                    placeholder={DOSIA_PLACEHOLDER}
                  />
                </FormField>
              </>
            )}
          </div>
        ) : null}

        {tab === "policy" ? (
          <div className="flex flex-col gap-3">
            {policyError ? (
              <ErrorBanner onDismiss={() => setPolicyError(null)}>
                {policyError}
              </ErrorBanner>
            ) : null}
            <InfoBanner>
              <span className="text-xs">
                此角色可以什么都不填 —— 员工会拿到默认工作面并能立即登录。下面全部是
                <b>可选</b>的权限收紧项，需要精细管控本角色时再展开配置。
              </span>
            </InfoBanner>
            <PolicyEditor
              value={policy}
              onChange={setPolicy}
              highRiskReadOnly={highRiskReadOnly}
              liveCatalog={liveCatalog}
            />
          </div>
        ) : null}

        {tab === "depts" ? (
          <FormField
            label="建议绑定的飞书部门"
            hint="勾选 → 写到 role.suggestedFeishuDeptIds；Phase F Agent 据此推荐分配员工"
          >
            <div className="border border-dosia-border rounded-md bg-dosia-bg-light p-2 max-h-[480px] overflow-auto">
              <FeishuDeptTree selected={deptIds} onToggle={toggleDept} />
            </div>
          </FormField>
        ) : null}

        {tab === "feishuBot" ? (
          <div className="flex flex-col gap-3">
            <InfoBanner>
              <span className="text-xs">
                配置该角色的飞书自建应用凭据（一角色一 App）。本期仅走飞书长连接，只需
                App ID + App Secret。开关开启且凭据配齐后，feishu-bot 进程会自动接入对应角色
                bot（约 30s 内热加载，无需重启）。
              </span>
            </InfoBanner>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={feishuEnabled}
                onChange={(e) => setFeishuEnabled(e.target.checked)}
                aria-label="启用飞书角色机器人"
              />
              <span className="text-sm text-dosia-text">启用飞书角色机器人</span>
            </label>
            <FormField label="App ID">
              <TextInput
                value={feishuAppId}
                onChange={(e) => setFeishuAppId(e.target.value)}
                placeholder="cli_xxxxxxxxxxxxxxxx"
              />
            </FormField>
            <FormField
              label="App Secret"
              hint={fbHasSecret ? "已配置；留空 = 不修改原密文" : "首次配置请填写"}
            >
              <TextInput
                type="password"
                value={feishuAppSecret}
                onChange={(e) => setFeishuAppSecret(e.target.value)}
                placeholder={fbHasSecret ? "••••••••（留空保留原值）" : "应用 App Secret"}
              />
            </FormField>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
